import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { toastValidationError } from '../../utils/toastValidationError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup, faPlus, faEdit, faTrash, faCheckCircle, faBan,
  faToggleOn, faToggleOff, faRotate, faSearch, faXmark, faUsers, faUniversity,
  faTriangleExclamation, faThumbtack, faCircleInfo, faClockRotateLeft,
  faChevronLeft, faChevronRight, faCalculator,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { swalConfirm } from '../../components/shared/swalConfirm';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  UiToggle,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import type { MerchantTier, TierRecomputeState, TierMemberMerchant, TierMembersResponse } from '../../types';
import { fmtBaht, fmtNum } from '../../utils/formatMoney';
import { readAdminUser, hasAdminPermission } from '../../utils/adminUser';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';

const TIER_STATUS_MAP: Record<string, StatusDef> = {
  active:   { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  icon: faCheckCircle, label: 'ใช้งาน'  },
  inactive: { bg: 'bg-gray-100 dark:bg-slate-700',      text: 'text-gray-600 dark:text-slate-400',   icon: faBan,         label: 'ปิดใช้' },
};

// ร้านค้าในระดับ (modal): สถานะร้าน + ที่มาของระดับ
const MEMBER_STATUS_MAP: Record<string, StatusDef> = {
  active:   { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle, label: 'ใช้งาน' },
  inactive: { bg: 'bg-red-100 dark:bg-red-900/30',     text: 'text-red-600 dark:text-red-400',     icon: faBan,         label: 'ระงับ'  },
};
const MEMBER_SOURCE_MAP: Record<string, StatusDef> = {
  computed: { bg: 'bg-gray-100 dark:bg-slate-700',        text: 'text-gray-600 dark:text-slate-300',   icon: faCalculator, label: 'คำนวณ' },
  override: { bg: 'bg-amber-50 dark:bg-amber-900/30',     text: 'text-amber-700 dark:text-amber-400',  icon: faThumbtack,  label: 'pin'   },
};
const MEMBERS_PAGE_SIZE = 100;
const MEMBERS_SEARCH_DEBOUNCE_MS = 300;

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 10 * 60 * 1000;
// 202 แค่เขียน Redis signal — watchdog ของ job หยิบทุก 15s (ADR-0007) → running_since ว่างได้ถึง ~15s หลังกด
// ถ้ายังไม่เห็นรอบใหม่เริ่มภายในช่วงนี้ค่อยเลิกรอ (กันค้างเมื่อ job ปิด/ตาย)
const POLL_GRACE_MS = 30 * 1000;

interface TierForm {
  name: string;
  rank: string;
  min_paid_orders: string;
  min_paid_amount: string;
  color: string;
  description: string;
  is_active: boolean;
}

const emptyForm = (): TierForm => ({
  name: '',
  rank: '',
  min_paid_orders: '0',
  min_paid_amount: '0',
  color: '#6366f1',
  description: '',
  is_active: true,
});

interface TierStatus {
  enabled: boolean;
  state: TierRecomputeState | null;
}

const fmtDateTime = (v?: string | null) => (v ? new Date(v).toLocaleString('th-TH') : '-');

/** actor จาก status เป็น 'admin:<uuid>' (ตัดคำไม่ได้บนมือถือ) → โชว์ 'admin:' + 8 ตัวแรก; ค่าเต็มอยู่ใน title */
const shortActor = (actor: string): string => {
  if (actor.startsWith('admin:')) {
    const id = actor.slice('admin:'.length);
    return id.length > 8 ? `admin:${id.slice(0, 8)}…` : actor;
  }
  return actor;
};

// หลังแก้เกณฑ์/เปิดใช้งาน: snapshot ใน merchants.tier_id ยังเป็นของก่อนแก้จนกว่าจะ recompute (ADR-0002) — บอกให้กดคำนวณใหม่
const RECOMPUTE_HINT = 'บันทึกแล้ว — กด "คำนวณใหม่ทั้งหมด" เพื่ออัปเดตระดับของร้านตามเกณฑ์ใหม่';

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** html สำหรับ swalConfirm ตอนปิดใช้งาน — แสดงผลกระทบจาก usage (ADR-0008) */
const buildDeactivateHtml = (tier: MerchantTier): string => {
  const u = tier.usage;
  const computed = u?.computed_count ?? 0;
  const override = u?.override_count ?? 0;
  const sole = u?.sole_tier_bank_ids?.length ?? 0;
  const rows = [
    `<li>ร้านที่คำนวณได้ระดับนี้ <b>${fmtNum(computed)}</b> ร้าน — จะตกไประดับต่ำกว่า<b>ทันที</b></li>`,
    `<li>ร้านที่ถูก pin ระดับนี้ <b>${fmtNum(override)}</b> ร้าน — จะไม่มีระดับจนกว่าจะ pin ใหม่</li>`,
    `<li>ธนาคารที่จำกัดเฉพาะระดับนี้ <b>${fmtNum(sole)}</b> บัญชี — จะไม่มีร้านไหนใช้ได้</li>`,
  ];
  return `<div style="text-align:left;font-size:14px"><p>ปิดใช้งาน "<b>${escapeHtml(tier.name)}</b>" มีผลทันทีโดยไม่ต้องรอคำนวณใหม่:</p><ul style="margin-top:8px;padding-left:20px;line-height:1.7">${rows.join('')}</ul></div>`;
};

const buildPayload = (form: TierForm) => ({
  name: form.name.trim(),
  rank: Number(form.rank),
  min_paid_orders: Number(form.min_paid_orders),
  min_paid_amount: Number(form.min_paid_amount),
  color: form.color.trim() || null,
  description: form.description.trim() || null,
  is_active: form.is_active,
});

/** ตรวจฝั่ง client ก่อนยิง (backend validate อีกชั้น + คืน errors[]) */
const validateForm = (form: TierForm): string | null => {
  if (!form.name.trim()) return 'กรุณาระบุชื่อระดับ';
  const rank = Number(form.rank);
  if (form.rank.trim() === '' || !Number.isInteger(rank)) return 'ลำดับ (rank) ต้องเป็นจำนวนเต็ม';
  const orders = Number(form.min_paid_orders);
  if (form.min_paid_orders.trim() === '' || !Number.isInteger(orders) || orders < 0) return 'จำนวน order ขั้นต่ำต้องเป็นจำนวนเต็ม ≥ 0';
  const amount = Number(form.min_paid_amount);
  if (form.min_paid_amount.trim() === '' || !Number.isFinite(amount) || amount < 0) return 'ยอดฝากขั้นต่ำต้องเป็นตัวเลข ≥ 0';
  return null;
};

const showWarnings = (warnings: unknown) => {
  if (Array.isArray(warnings)) warnings.forEach((w) => toast.warn(String(w)));
};

interface FormFieldsProps {
  form: TierForm;
  setForm: React.Dispatch<React.SetStateAction<TierForm>>;
}

const FormFields: React.FC<FormFieldsProps> = ({ form, setForm }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className={LABEL_CLASS}>
          ชื่อระดับ <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          required
          placeholder="เช่น Gold"
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>
          ลำดับ (rank) <span className="text-red-400">*</span>
        </label>
        <input
          type="number"
          step="1"
          value={form.rank}
          onChange={(e) => setForm((p) => ({ ...p, rank: e.target.value }))}
          required
          placeholder="สูงกว่า = ดีกว่า"
          className={INPUT_CLASS}
        />
        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">ห้ามซ้ำกับระดับอื่น · ตัวเลขสูงกว่า = ระดับสูงกว่า</p>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className={LABEL_CLASS}>
          order ที่ paid ขั้นต่ำ <span className="text-red-400">*</span>
        </label>
        <input
          type="number"
          min="0"
          step="1"
          value={form.min_paid_orders}
          onChange={(e) => setForm((p) => ({ ...p, min_paid_orders: e.target.value }))}
          required
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label className={LABEL_CLASS}>
          ยอดฝากรวมขั้นต่ำ (บาท) <span className="text-red-400">*</span>
        </label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={form.min_paid_amount}
          onChange={(e) => setForm((p) => ({ ...p, min_paid_amount: e.target.value }))}
          required
          className={INPUT_CLASS}
        />
      </div>
      <p className="col-span-2 text-[11px] text-gray-400 dark:text-slate-500 -mt-2">
        ร้านต้องถึงเกณฑ์<b>ทั้งสองข้อ</b> (สะสมตลอดชีพจาก order ที่ paid) จึงจะได้ระดับนี้
      </p>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className={LABEL_CLASS}>สี</label>
        <div className="flex items-center gap-2">
          <span
            className="w-8 h-8 rounded-lg border border-gray-200 dark:border-slate-600 flex-shrink-0"
            style={{ background: form.color.trim() || 'transparent' }}
            title="ตัวอย่างสี"
          />
          <input
            type="text"
            value={form.color}
            onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
            placeholder="#6366f1"
            className={INPUT_CLASS}
          />
        </div>
      </div>
      <div className="flex items-end">
        <div className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg">
          <span className="text-sm text-gray-700 dark:text-slate-200">เปิดใช้งาน</span>
          <UiToggle
            checked={form.is_active}
            onChange={(next) => setForm((p) => ({ ...p, is_active: next }))}
            size="sm"
            ariaLabel="เปิดใช้งานระดับ"
          />
        </div>
      </div>
    </div>

    <div>
      <label className={LABEL_CLASS}>คำอธิบาย</label>
      <textarea
        value={form.description}
        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        rows={2}
        placeholder="ไม่บังคับ"
        className={INPUT_CLASS}
      />
    </div>
  </>
);

const TiersPage: React.FC = () => {
  const adminUser = useMemo(() => readAdminUser(), []);
  const canManageTiers = hasAdminPermission(adminUser, 'system_settings');

  const [tiers, setTiers] = useState<MerchantTier[]>([]);
  const [supported, setSupported] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [status, setStatus] = useState<TierStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  // สั่ง 202 แล้วแต่ job ยังไม่รับ (running_since ยังว่าง) — ใช้แสดง "กำลังคำนวณ…" แทน state ปลอม
  const [recomputeQueued, setRecomputeQueued] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState<MerchantTier | null>(null);
  const [form, setForm] = useState<TierForm>(emptyForm());

  // modal "ร้านค้าในระดับ" — โหลดจาก GET /admin/merchant-tiers/:id/merchants (ค้นหา debounce + แบ่งหน้าฝั่ง server)
  const [membersTier, setMembersTier] = useState<MerchantTier | null>(null);
  const [members, setMembers] = useState<TierMemberMerchant[]>([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersSupported, setMembersSupported] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersQuery, setMembersQuery] = useState('');
  const [membersDebouncedQuery, setMembersDebouncedQuery] = useState('');
  const [membersOffset, setMembersOffset] = useState(0);
  // โหลดพัง (500/403) ต้องแสดงเป็น error ไม่ใช่ "ยังไม่มีร้านค้า" — เก็บข้อความจาก backend + ปุ่มลองใหม่ (bump membersReload ให้ effect ยิงซ้ำ)
  const [membersError, setMembersError] = useState<string | null>(null);
  const [membersReload, setMembersReload] = useState(0);
  // response ที่มาช้า (ปิด modal / เปลี่ยนคำค้น / เปลี่ยนหน้าไปแล้ว) ต้องถูกทิ้ง
  const membersReqSeqRef = useRef(0);

  const mountedRef = useRef(true);
  const pollRef = useRef<number | null>(null);
  const pollStartRef = useRef(0);
  // ข้อมูลรอบที่กดสั่ง: last_attempt_at ก่อนกด (เทียบฝั่ง server ไม่ต้องกลัว clock skew) + เคยเห็น running_since แล้วหรือยัง
  const pollBaselineAttemptRef = useRef<string | null>(null);
  const pollSawRunningRef = useRef(false);

  const fetchTiers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await merchantApi.get('/admin/merchant-tiers');
      if (!mountedRef.current) return;
      const isSupported = res.data?.supported === true;
      setSupported(isSupported);
      setTiers(isSupported ? (res.data.tiers || []) : []);
    } catch (err: any) {
      if (!mountedRef.current) return;
      toast.error(err.response?.data?.message || 'โหลดข้อมูลระดับร้านค้าไม่สำเร็จ');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const fetchStatus = useCallback(async (silent = false): Promise<TierStatus | null> => {
    if (!silent) setStatusLoading(true);
    try {
      const res = await merchantApi.get('/admin/merchant-tiers/status');
      if (!mountedRef.current) return null;
      if (res.data?.supported === false) {
        setStatus(null);
        return null;
      }
      const next: TierStatus = { enabled: res.data?.enabled === true, state: res.data?.state ?? null };
      setStatus(next);
      return next;
    } catch (err: any) {
      if (!mountedRef.current) return null;
      if (!silent) toast.error(err.response?.data?.message || 'โหลดสถานะการคำนวณไม่สำเร็จ');
      return null;
    } finally {
      if (!silent && mountedRef.current) setStatusLoading(false);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setRecomputeQueued(false);
  }, []);

  /**
   * poll /status ทุก 3 วิ จนรอบคำนวณจบ
   * @param queued true = เพิ่งสั่งผ่าน 202 (job ยังไม่รับ) → หยุดเฉพาะเมื่อ running_since ว่าง **และ** รู้แล้วว่ารอบใหม่เกิดขึ้นจริง
   *   (เคยเห็น running_since / last_attempt_at ขยับจากค่าก่อนกด) หรือเลย grace 30s — ไม่งั้น poll แรกหลัง 3 วิจะหยุดก่อน watchdog 15s รับงาน
   *   false = ติดตามรอบที่วิ่งอยู่แล้ว → หยุดทันทีที่ running_since ว่าง
   */
  const startPolling = useCallback((queued = false, baselineAttemptAt: string | null = null) => {
    if (pollRef.current != null) return;
    pollStartRef.current = Date.now();
    pollBaselineAttemptRef.current = baselineAttemptAt;
    pollSawRunningRef.current = !queued;
    setRecomputeQueued(queued);
    pollRef.current = window.setInterval(async () => {
      if (Date.now() - pollStartRef.current > POLL_MAX_MS) {
        stopPolling();
        return;
      }
      const s = await fetchStatus(true);
      if (!mountedRef.current || !s) return;
      if (s.state?.running_since != null) {
        pollSawRunningRef.current = true;
        setRecomputeQueued(false);
        return;
      }
      const attemptMoved =
        (s.state?.last_attempt_at ?? null) !== pollBaselineAttemptRef.current;
      const graceOver = Date.now() - pollStartRef.current > POLL_GRACE_MS;
      if (pollSawRunningRef.current || attemptMoved || graceOver) {
        stopPolling();
        fetchTiers(); // usage counts เปลี่ยนหลังคำนวณเสร็จ
      }
    }, POLL_INTERVAL_MS);
  }, [fetchStatus, fetchTiers, stopPolling]);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      await fetchTiers();
      const s = await fetchStatus(true);
      if (s?.state?.running_since != null) startPolling();
    })();
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [fetchTiers, fetchStatus, startPolling, stopPolling]);

  const isRunning = status?.state?.running_since != null || recomputeQueued;

  // ระดับที่ถูกแก้หลังรอบคำนวณสำเร็จล่าสุด → usage count / badge ในหน้าร้านค้ายังเป็น snapshot ก่อนแก้ (ADR-0002)
  // ไม่มี last_success_at (ยังไม่เคยคำนวณ) → ไม่ถือว่า stale — ข้อความ "ยังไม่เคยคำนวณ" ครอบอยู่แล้ว
  const lastSuccessMs = useMemo(() => {
    const v = status?.state?.last_success_at;
    if (!v) return null;
    const ms = new Date(v).getTime();
    return Number.isNaN(ms) ? null : ms;
  }, [status?.state?.last_success_at]);
  const staleTiers = useMemo(() => {
    if (lastSuccessMs === null) return [] as MerchantTier[];
    return tiers.filter((t) => {
      if (!t.updated_at) return false;
      const ms = new Date(t.updated_at).getTime();
      return !Number.isNaN(ms) && ms > lastSuccessMs;
    });
  }, [tiers, lastSuccessMs]);
  const staleTierIds = useMemo(() => new Set(staleTiers.map((t) => t.id)), [staleTiers]);

  // debounce คำค้นใน modal — ค่าที่ยิงจริงเปลี่ยนแล้วกลับไปหน้าแรกเสมอ
  useEffect(() => {
    if (membersTier === null) return;
    const timer = window.setTimeout(() => {
      setMembersDebouncedQuery(membersQuery.trim());
      setMembersOffset(0);
    }, MEMBERS_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [membersQuery, membersTier]);

  useEffect(() => {
    if (membersTier === null) return;
    const tierId = membersTier.id;
    const seq = ++membersReqSeqRef.current;
    const isStale = () => !mountedRef.current || seq !== membersReqSeqRef.current;
    (async () => {
      setMembersLoading(true);
      setMembersError(null);
      try {
        const res = await merchantApi.get<TierMembersResponse>(`/admin/merchant-tiers/${tierId}/merchants`, {
          params: {
            q: membersDebouncedQuery || undefined,
            limit: MEMBERS_PAGE_SIZE,
            offset: membersOffset,
          },
        });
        if (isStale()) return;
        const supportedNow = res.data?.supported !== false;
        setMembersSupported(supportedNow);
        setMembers(supportedNow && Array.isArray(res.data?.data) ? res.data.data : []);
        setMembersTotal(supportedNow ? Number(res.data?.total ?? 0) : 0);
      } catch (err: any) {
        if (isStale()) return;
        const message = err.response?.data?.message || 'โหลดรายชื่อร้านค้าในระดับไม่สำเร็จ';
        setMembers([]);
        setMembersTotal(0);
        setMembersError(message);
        toast.error(message);
      } finally {
        if (!isStale()) setMembersLoading(false);
      }
    })();
  }, [membersTier, membersDebouncedQuery, membersOffset, membersReload]);

  const openMembers = (tier: MerchantTier) => {
    setMembers([]);
    setMembersTotal(0);
    setMembersSupported(true);
    setMembersError(null);
    setMembersQuery('');
    setMembersDebouncedQuery('');
    setMembersOffset(0);
    setMembersTier(tier);
  };

  const closeMembers = () => {
    membersReqSeqRef.current += 1; // ทิ้ง response ที่ยังค้าง
    setMembersTier(null);
    setMembersLoading(false);
  };

  const openCreate = () => {
    setForm(emptyForm());
    setShowCreate(true);
  };

  const openEdit = (tier: MerchantTier) => {
    setEditing(tier);
    setForm({
      name: tier.name ?? '',
      rank: String(tier.rank ?? ''),
      min_paid_orders: String(tier.min_paid_orders ?? 0),
      min_paid_amount: String(tier.min_paid_amount ?? 0),
      color: tier.color ?? '',
      description: tier.description ?? '',
      is_active: tier.is_active !== false,
    });
    setShowEdit(true);
  };

  const closeCreate = () => {
    if (saving) return;
    setShowCreate(false);
  };

  const closeEdit = () => {
    if (saving) return;
    setShowEdit(false);
    setEditing(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validateForm(form);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setSaving(true);
    try {
      const res = await merchantApi.post('/admin/merchant-tiers', buildPayload(form));
      toast.success('สร้างระดับร้านค้าสำเร็จ');
      showWarnings(res.data?.warnings);
      toast.info(RECOMPUTE_HINT);
      setShowCreate(false);
      fetchTiers();
    } catch (err: any) {
      toastValidationError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const invalid = validateForm(form);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    // ปิดใช้งานผ่านฟอร์ม (true→false) ต้อง confirm ผลกระทบเหมือนปุ่มเปิด-ปิดในตาราง
    const deactivating = editing.is_active && !form.is_active;
    if (deactivating) {
      const ok = await swalConfirm({
        title: 'ปิดใช้งานระดับนี้?',
        html: buildDeactivateHtml(editing),
        icon: 'warning',
        confirmText: 'ปิดใช้งานและบันทึก',
        confirmColor: '#f59e0b',
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const res = await merchantApi.put(`/admin/merchant-tiers/${editing.id}`, buildPayload(form));
      toast.success('บันทึกระดับร้านค้าสำเร็จ');
      showWarnings(res.data?.warnings);
      // ปิดใช้งานมีผลทันที (confirm บอกแล้ว) — ไม่ต้องเตือนซ้ำ
      if (!deactivating) toast.info(RECOMPUTE_HINT);
      setShowEdit(false);
      setEditing(null);
      fetchTiers();
    } catch (err: any) {
      toastValidationError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (tier: MerchantTier) => {
    const next = !tier.is_active;
    if (!next) {
      const ok = await swalConfirm({
        title: 'ปิดใช้งานระดับนี้?',
        html: buildDeactivateHtml(tier),
        icon: 'warning',
        confirmText: 'ปิดใช้งาน',
        confirmColor: '#f59e0b',
      });
      if (!ok) return;
    }
    setBusyId(tier.id);
    try {
      const res = await merchantApi.put(`/admin/merchant-tiers/${tier.id}`, { is_active: next });
      toast.success(next ? 'เปิดใช้งานระดับแล้ว' : 'ปิดใช้งานระดับแล้ว');
      showWarnings(res.data?.warnings);
      if (next) toast.info(RECOMPUTE_HINT);
      fetchTiers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'เปลี่ยนสถานะไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (tier: MerchantTier) => {
    const ok = await swalConfirm({
      title: `ลบระดับ "${tier.name}"?`,
      text: 'ลบได้เฉพาะระดับที่ไม่มีธนาคารจำกัดไว้และไม่มีร้านถูก pin — ประวัติการเปลี่ยนระดับของร้านยังคงอยู่',
      icon: 'warning',
      confirmText: 'ลบ',
      confirmColor: '#ef4444',
    });
    if (!ok) return;
    setBusyId(tier.id);
    try {
      await merchantApi.delete(`/admin/merchant-tiers/${tier.id}`);
      toast.success('ลบระดับร้านค้าแล้ว');
      fetchTiers();
    } catch (err: any) {
      const data = err.response?.data;
      let msg: string = data?.message || 'ลบไม่สำเร็จ';
      // 409 in-use: backend ส่ง { banks, merchants } มาด้วย — message ปกติมีตัวเลขอยู่แล้ว เติมเฉพาะเมื่อยังไม่มี
      const hasCounts = typeof data?.banks === 'number' || typeof data?.merchants === 'number';
      const msgHasCounts =
        !!data?.message &&
        data.message.includes(String(data?.banks ?? 0)) &&
        data.message.includes(String(data?.merchants ?? 0));
      if (hasCounts && !msgHasCounts) {
        msg += ` (ธนาคาร ${fmtNum(data?.banks ?? 0)} · ร้านที่ pin ${fmtNum(data?.merchants ?? 0)})`;
      }
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const handleRecomputeAll = async () => {
    const ok = await swalConfirm({
      title: 'คำนวณระดับใหม่ทั้งหมด?',
      text: 'ระบบจะนับยอดสะสมของทุกร้านใหม่แบบ background — ใช้เวลาไม่กี่วินาทีถึงหลายนาทีตามขนาดข้อมูล',
      icon: 'question',
      confirmText: 'เริ่มคำนวณ',
    });
    if (!ok) return;
    setRecomputing(true);
    try {
      await merchantApi.post('/admin/merchant-tiers/recompute', {});
      toast.info('เริ่มคำนวณแล้ว — กำลังติดตามสถานะ');
      // job รับ signal ผ่าน watchdog (ไม่ทันที) — poll แบบ queued จนเห็นรอบใหม่เริ่มแล้วจบ
      startPolling(true, status?.state?.last_attempt_at ?? null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'สั่งคำนวณไม่สำเร็จ');
      if (err.response?.status === 409) {
        // มีรอบวิ่งอยู่แล้ว → ติดตามรอบนั้นแทน
        const s = await fetchStatus(true);
        if (s?.state?.running_since != null) startPolling();
      }
    } finally {
      setRecomputing(false);
    }
  };

  const filteredTiers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tiers;
    return tiers.filter((t) =>
      t.name.toLowerCase().includes(q) ||
      (t.description ?? '').toLowerCase().includes(q)
    );
  }, [tiers, searchQuery]);

  const activeCount = tiers.filter((t) => t.is_active).length;
  const computedTotal = tiers.reduce((s, t) => s + (t.usage?.computed_count ?? 0), 0);
  const overrideTotal = tiers.reduce((s, t) => s + (t.usage?.override_count ?? 0), 0);
  const bankLinkTotal = tiers.reduce((s, t) => s + (t.usage?.bank_count ?? 0), 0);

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faLayerGroup,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'จำนวนระดับ',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtNum(tiers.length)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ใช้งาน {fmtNum(activeCount)} · ปิด {fmtNum(tiers.length - activeCount)}</span>,
    },
    {
      icon: faUsers,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'ร้านที่มีระดับ (คำนวณได้)',
      value: <span className="text-emerald-600 dark:text-emerald-400">{fmtNum(computedTotal)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">pin โดยแอดมิน {fmtNum(overrideTotal)}</span>,
    },
    {
      icon: faUniversity,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'ธนาคารที่จำกัดระดับ',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtNum(bankLinkTotal)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">นับรวมทุกระดับ (บัญชีที่รับหลายระดับนับซ้ำ)</span>,
    },
  ], [tiers.length, activeCount, computedTotal, overrideTotal, bankLinkTotal]);

  const columns: Column<MerchantTier>[] = useMemo(() => [
    // ทุกคอลัมน์บังคับบรรทัดเดียว (whitespace-nowrap) — DataTable ครอบด้วย overflow-x-auto ให้ตารางเลื่อนแนวนอนแทนการตัดคำบนมือถือ
    {
      key: 'rank',
      header: 'ลำดับ',
      sortable: true,
      width: '64px',
      className: 'whitespace-nowrap',
      render: (t) => (
        <span className="font-mono text-[13px] font-bold text-gray-700 dark:text-slate-200">{t.rank}</span>
      ),
    },
    {
      key: 'name',
      header: 'ชื่อระดับ',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (t) => (
        <div className="flex items-center gap-2.5">
          <span
            className="w-3.5 h-3.5 rounded-full border border-black/10 dark:border-white/10 flex-shrink-0"
            style={{ background: t.color || '#9ca3af' }}
          />
          <div className="max-w-[220px]">
            <p className="text-[13px] font-semibold text-gray-800 dark:text-slate-100 truncate" title={t.name}>{t.name}</p>
            {t.description && (
              <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate" title={t.description}>{t.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'min_paid_orders',
      header: 'เกณฑ์ขั้นต่ำ',
      sortable: true,
      className: 'whitespace-nowrap',
      render: (t) => (
        <span className="text-[12px] text-gray-700 dark:text-slate-200">
          ≥ {fmtNum(t.min_paid_orders)} order
          <span className="text-gray-300 dark:text-slate-600 mx-1">·</span>
          <span className="text-gray-500 dark:text-slate-400">≥ {fmtBaht(t.min_paid_amount)}</span>
        </span>
      ),
    },
    {
      key: 'usage',
      header: 'การใช้งาน',
      className: 'whitespace-nowrap',
      render: (t) => {
        const u = t.usage;
        if (!u) return <span className="text-[11px] text-gray-300 dark:text-slate-600">-</span>;
        const sole = u.sole_tier_bank_ids?.length ?? 0;
        const stale = staleTierIds.has(t.id);
        const pillClass = 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200';
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openMembers(t); }}
            className="inline-flex items-center gap-1 flex-nowrap text-[11px] whitespace-nowrap hover:opacity-80 transition-opacity"
            title="คลิกเพื่อดูรายชื่อร้านค้าในระดับนี้"
          >
            <span className={pillClass} title="ร้านที่คำนวณได้ระดับนี้">
              <FontAwesomeIcon icon={faCalculator} className="text-[9px] text-gray-400 dark:text-slate-400" />
              <b>{fmtNum(u.computed_count)}</b>
            </span>
            <span className={pillClass} title="ร้านที่ pin">
              <FontAwesomeIcon icon={faThumbtack} className="text-[9px] text-amber-500" />
              <b>{fmtNum(u.override_count)}</b>
            </span>
            <span className={pillClass} title="ธนาคารที่อนุญาตระดับนี้">
              <FontAwesomeIcon icon={faUniversity} className="text-[9px] text-blue-500" />
              <b>{fmtNum(u.bank_count)}</b>
            </span>
            {sole > 0 && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium"
                title={`ธนาคาร ${fmtNum(sole)} บัญชีรับเฉพาะระดับนี้ — ปิด/ลบระดับแล้วจะไม่มีร้านไหนใช้ได้`}
              >
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-[9px]" />
                <span className="hidden md:inline">เฉพาะระดับนี้ {fmtNum(sole)} บัญชี</span>
              </span>
            )}
            {stale && (
              <span
                className="inline-flex w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"
                title="แก้ไขหลังรอบคำนวณล่าสุด"
                aria-label="แก้ไขหลังรอบคำนวณล่าสุด"
              />
            )}
          </button>
        );
      },
    },
    {
      key: 'is_active',
      header: 'สถานะ',
      sortable: true,
      width: '100px',
      className: 'whitespace-nowrap',
      render: (t) => <StatusBadge status={t.is_active ? 'active' : 'inactive'} statusMap={TIER_STATUS_MAP} />,
    },
    {
      key: 'actions',
      header: 'จัดการ',
      width: '230px',
      className: 'whitespace-nowrap',
      render: (t) => (
        <div className="flex items-center gap-1 flex-nowrap">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openMembers(t); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors flex items-center gap-1"
            title="ดูรายชื่อร้านค้าในระดับนี้"
          >
            <FontAwesomeIcon icon={faUsers} className="text-[10px]" />
            ดูร้านค้า
          </button>
          {canManageTiers && (
          <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openEdit(t); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
            title="แก้ไข"
          >
            <FontAwesomeIcon icon={faEdit} className="text-[10px]" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleToggleActive(t); }}
            disabled={busyId === t.id}
            className={`px-2 py-1 rounded-md text-[11px] font-medium transition-colors flex items-center gap-1 disabled:opacity-50 ${
              t.is_active
                ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
                : 'bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600'
            }`}
            title={t.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
          >
            <FontAwesomeIcon icon={t.is_active ? faToggleOn : faToggleOff} className="text-[10px]" />
            {t.is_active ? 'เปิด' : 'ปิด'}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDelete(t); }}
            disabled={busyId === t.id}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
            title="ลบ"
          >
            <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
          </button>
          </>
          )}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [busyId, canManageTiers, staleTierIds]);

  const lastResult = status?.state?.last_result ?? null;
  const showStaleNotice = staleTiers.length > 0 && !isRunning;

  // มือถือ: หัวเรื่อง+badge → รายละเอียดแบบ label/value → (แจ้ง stale) → ปุ่มเรียงลงมาเต็มความกว้าง; md+: ปุ่มไปอยู่ขวา
  const renderStatusPanel = () => (
    <div className="mb-6 bg-white dark:bg-slate-800 rounded-2xl ring-1 ring-gray-100 dark:ring-slate-700 shadow-sm p-4 overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0 flex-1 break-words">
          <div className="flex items-center gap-2 flex-wrap">
            <FontAwesomeIcon icon={faClockRotateLeft} className="text-gray-400 dark:text-slate-500" />
            <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">การคำนวณระดับ (รอบ 01:00 ทุกคืน)</h3>
            {status && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                status.enabled
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
              }`}>
                job {status.enabled ? 'เปิด' : 'ปิด'}
              </span>
            )}
            {isRunning && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 animate-pulse">
                <FontAwesomeIcon icon={faRotate} className="animate-spin mr-1" />
                {status?.state?.running_since != null
                  ? `กำลังคำนวณ… (เริ่ม ${fmtDateTime(status.state.running_since)})`
                  : 'กำลังคำนวณ… (รอ job รับคำสั่ง)'}
              </span>
            )}
          </div>
          <div className="mt-2 text-[12px] text-gray-500 dark:text-slate-400">
            {status?.state ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 min-w-0">
                <dt className="whitespace-nowrap">สำเร็จล่าสุด</dt>
                <dd className="min-w-0 break-words">
                  <b className="text-gray-700 dark:text-slate-200">{fmtDateTime(status.state.last_success_at)}</b>
                </dd>
                {status.state.trigger && (
                  <>
                    <dt className="whitespace-nowrap">trigger</dt>
                    <dd className="min-w-0 break-words text-gray-400 dark:text-slate-500">{status.state.trigger}</dd>
                  </>
                )}
                {status.state.actor && (
                  <>
                    <dt className="whitespace-nowrap">โดย</dt>
                    <dd className="min-w-0">
                      <span className="block truncate max-w-full font-mono text-gray-400 dark:text-slate-500" title={status.state.actor}>
                        {shortActor(status.state.actor)}
                      </span>
                    </dd>
                  </>
                )}
                {lastResult && (
                  <>
                    <dt className="whitespace-nowrap">ผลล่าสุด</dt>
                    <dd className="min-w-0 whitespace-normal break-words">
                      ประมวลผล <b className="text-gray-700 dark:text-slate-200">{fmtNum(lastResult.processed)}</b> ร้าน
                      <span className="text-gray-300 dark:text-slate-600 mx-1">·</span>
                      เปลี่ยนระดับ <b className="text-gray-700 dark:text-slate-200">{fmtNum(lastResult.changed)}</b>
                      <span className="text-gray-300 dark:text-slate-600 mx-1">·</span>
                      {fmtNum(lastResult.batches)} batch
                      <span className="text-gray-300 dark:text-slate-600 mx-1">·</span>
                      {fmtNum(lastResult.duration_ms)} ms
                      {lastResult.ok === false && (
                        <span className="ml-1 text-red-500">(ไม่สำเร็จ{lastResult.code ? `: ${lastResult.code}` : ''})</span>
                      )}
                    </dd>
                  </>
                )}
                {status.state.error && (
                  <>
                    <dt className="whitespace-nowrap text-red-500 dark:text-red-400">ข้อผิดพลาดล่าสุด</dt>
                    <dd className="min-w-0 text-red-500 dark:text-red-400 break-all">{status.state.error}</dd>
                  </>
                )}
                {status.state.last_attempt_at && !status.state.last_success_at && (
                  <>
                    <dt className="whitespace-nowrap">พยายามล่าสุด</dt>
                    <dd className="min-w-0 break-words">{fmtDateTime(status.state.last_attempt_at)}</dd>
                  </>
                )}
              </dl>
            ) : (
              <p>ยังไม่เคยคำนวณ — กด "คำนวณใหม่ทั้งหมด" หลังสร้างระดับครั้งแรก</p>
            )}
          </div>
          {showStaleNotice && (
            <div className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-[12px] text-amber-800 dark:text-amber-200 flex items-start gap-2 break-words">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0 text-amber-500" />
              <div className="min-w-0 flex-1">
                <span>
                  มี <b>{fmtNum(staleTiers.length)}</b> ระดับที่แก้ไขหลังรอบคำนวณล่าสุด — ตัวเลขการใช้งานและระดับที่แสดงในหน้าร้านค้าอาจยังเป็นของก่อนแก้ (การเลือกธนาคารใช้เกณฑ์ใหม่ทันทีอยู่แล้ว) → กด "คำนวณใหม่ทั้งหมด"
                </span>
                {canManageTiers && (
                  <button
                    type="button"
                    onClick={handleRecomputeAll}
                    disabled={recomputing || isRunning}
                    className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 font-semibold hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors disabled:opacity-50 whitespace-nowrap align-baseline"
                  >
                    <FontAwesomeIcon icon={faRotate} className={`text-[10px] ${recomputing ? 'animate-spin' : ''}`} />
                    คำนวณใหม่ทั้งหมด
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:flex-shrink-0">
          <UiButton
            variant="secondary"
            icon={faRotate}
            loading={statusLoading}
            onClick={() => fetchStatus()}
            className="w-full sm:w-auto justify-center"
          >
            รีเฟรชสถานะ
          </UiButton>
          {canManageTiers && (
            <UiButton
              icon={faRotate}
              loading={recomputing}
              disabled={isRunning}
              onClick={handleRecomputeAll}
              className="w-full sm:w-auto justify-center"
            >
              คำนวณใหม่ทั้งหมด
            </UiButton>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faLayerGroup}
        title="ระดับร้านค้า"
        subtitle={`กำหนดระดับตามยอดสะสมของร้าน และจำกัดธนาคารฝากตามระดับ • ${fmtNum(tiers.length)} ระดับ`}
        actions={
          canManageTiers && supported ? (
            <UiButton icon={faPlus} onClick={openCreate}>
              เพิ่มระดับ
            </UiButton>
          ) : undefined
        }
      />

      {!supported && !loading ? (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 flex items-start gap-4">
          <FontAwesomeIcon icon={faCircleInfo} className="text-2xl text-amber-500 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-200 space-y-1">
            <p className="font-semibold">tenant นี้ยังไม่ได้ติดตั้งระบบระดับร้านค้า (migration ยังไม่ลง)</p>
            <p className="text-amber-700/80 dark:text-amber-300/80">
              ระบบยังทำงานปกติในโหมดไม่จำกัดระดับ — ธนาคารทุกบัญชีเปิดให้ทุกร้านที่ผูกไว้ใช้ได้ · เมื่อ migrate ฐานข้อมูลครบ (ตาราง merchant_tiers + คอลัมน์บน merchants/banks + RPC) หน้านี้จะแสดงตารางอัตโนมัติ
            </p>
          </div>
        </div>
      ) : (
        <>
          <UiStatsGrid cards={statCards} loading={loading} cols={3} />

          {renderStatusPanel()}

          <UiFilterPanel
            onSearch={() => {}}
            onReset={() => setSearchQuery('')}
            loading={loading}
            actions={
              <button
                type="button"
                onClick={() => fetchTiers()}
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
            }
          >
            <div className="flex flex-wrap items-end gap-4 w-fit">
              <div className="w-[280px] shrink-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  <FontAwesomeIcon icon={faSearch} className="mr-1" />
                  ค้นหา
                </label>
                <div className="relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ค้นหาชื่อระดับ, คำอธิบาย..."
                    className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600 dark:focus:border-slate-500"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
                    >
                      <FontAwesomeIcon icon={faXmark} className="text-sm" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </UiFilterPanel>

          <DataTable
            key={searchQuery}
            data={filteredTiers}
            columns={columns}
            loading={loading}
            keyExtractor={(t) => t.id}
            emptyMessage={searchQuery ? 'ไม่พบระดับที่ค้นหา' : 'ยังไม่มีระดับร้านค้า — ทุกร้านใช้ธนาคารที่ผูกไว้ได้ตามปกติ'}
            emptyIcon={<FontAwesomeIcon icon={faLayerGroup} className="text-5xl text-gray-200 dark:text-slate-600" />}
            hoverable
            compact
            pagination={{
              enabled: true,
              pageSize: 20,
              pageSizeOptions: [10, 20, 50],
              showPageSizeSelector: true,
              showInfo: true,
              showFirstLast: true,
            }}
          />
        </>
      )}

      <UiModal
        open={showCreate}
        onClose={closeCreate}
        title="เพิ่มระดับร้านค้า"
        icon={faLayerGroup}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={closeCreate} disabled={saving} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              loading={saving}
              onClick={() => (document.getElementById('tier-create-form') as HTMLFormElement | null)?.requestSubmit()}
              className="flex-1 justify-center"
            >
              สร้างระดับ
            </UiButton>
          </div>
        }
      >
        <form id="tier-create-form" onSubmit={handleCreate} className="space-y-4">
          <FormFields form={form} setForm={setForm} />
        </form>
      </UiModal>

      <UiModal
        open={showEdit}
        onClose={closeEdit}
        title={`แก้ไขระดับ${editing ? ` — ${editing.name}` : ''}`}
        icon={faEdit}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={closeEdit} disabled={saving} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              loading={saving}
              onClick={() => (document.getElementById('tier-edit-form') as HTMLFormElement | null)?.requestSubmit()}
              className="flex-1 justify-center"
            >
              บันทึกการแก้ไข
            </UiButton>
          </div>
        }
      >
        <form id="tier-edit-form" onSubmit={handleEdit} className="space-y-4">
          <FormFields form={form} setForm={setForm} />
          {editing?.usage && (
            <p className="text-[11px] text-gray-400 dark:text-slate-500">
              ใช้งานอยู่: คำนวณได้ {fmtNum(editing.usage.computed_count)} ร้าน · pin {fmtNum(editing.usage.override_count)} ร้าน · ธนาคาร {fmtNum(editing.usage.bank_count)} บัญชี — แก้เกณฑ์/ปิดใช้งานมีผลกับการเลือกธนาคารทันที
            </p>
          )}
        </form>
      </UiModal>

      <UiModal
        open={membersTier !== null}
        onClose={closeMembers}
        title={`ร้านค้าในระดับ ${membersTier?.name ?? ''}`}
        icon={faUsers}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={closeMembers} className="flex-1 justify-center">
              ปิด
            </UiButton>
          </div>
        }
      >
        {membersTier !== null && (() => {
          const pageStart = membersTotal === 0 ? 0 : membersOffset + 1;
          const pageEnd = Math.min(membersOffset + members.length, membersTotal);
          const hasPrev = membersOffset > 0;
          const hasNext = membersOffset + MEMBERS_PAGE_SIZE < membersTotal;
          return (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="relative w-full sm:w-[300px]">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
                  <input
                    type="text"
                    value={membersQuery}
                    onChange={(e) => setMembersQuery(e.target.value)}
                    placeholder="ค้นหาชื่อ, อีเมล, บริษัท..."
                    className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600 dark:focus:border-slate-500"
                  />
                  {membersQuery && (
                    <button
                      type="button"
                      onClick={() => setMembersQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
                    >
                      <FontAwesomeIcon icon={faXmark} className="text-sm" />
                    </button>
                  )}
                </div>
                <p className="text-[12px] text-gray-500 dark:text-slate-400">
                  ทั้งหมด <b className="text-gray-700 dark:text-slate-200">{fmtNum(membersTotal)}</b> ร้าน
                  {membersTier.is_active === false && (
                    <span className="ml-2 px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-[10px]">ระดับนี้ปิดใช้งาน</span>
                  )}
                </p>
              </div>
              {/* คอลัมน์ "การใช้งาน" นับ computed/pin แยกกัน (rpc_merchant_tier_usage_v1) แต่รายชื่อนี้นับตามระดับที่มีผลจริง — ตัวเลขต่างกันได้ ไม่ใช่รายการหาย */}
              <p className="text-[11px] text-gray-400 dark:text-slate-500 flex items-start gap-1.5">
                <FontAwesomeIcon icon={faCircleInfo} className="mt-0.5 shrink-0" />
                <span>นับตามระดับที่มีผลจริง (pin มาก่อนคำนวณ) — ตัวเลขอาจต่างจากคอลัมน์การใช้งาน ซึ่งนับร้านที่คำนวณได้ระดับนี้แม้ถูก pin ไประดับอื่น</span>
              </p>

              {!membersSupported ? (
                <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
                  tenant นี้ยังไม่ได้ติดตั้งระบบระดับร้านค้า — ไม่มีข้อมูลร้านค้าในระดับ
                </p>
              ) : membersLoading ? (
                <div className="py-10 text-center text-sm text-gray-400 dark:text-slate-500">
                  <FontAwesomeIcon icon={faRotate} className="animate-spin mr-2" />
                  กำลังโหลด...
                </div>
              ) : membersError !== null ? (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 p-4 text-center">
                  <p className="text-sm text-red-600 dark:text-red-300 flex items-center justify-center gap-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    {membersError}
                  </p>
                  <UiButton
                    variant="secondary"
                    icon={faRotate}
                    onClick={() => setMembersReload((n) => n + 1)}
                    className="mt-3 justify-center"
                  >
                    ลองใหม่
                  </UiButton>
                </div>
              ) : members.length === 0 ? (
                <div className="py-10 text-center">
                  <FontAwesomeIcon icon={faUsers} className="text-4xl text-gray-200 dark:text-slate-600 mb-2" />
                  <p className="text-sm text-gray-400 dark:text-slate-500">
                    {membersDebouncedQuery ? 'ไม่พบร้านค้าที่ค้นหา' : 'ยังไม่มีร้านค้าในระดับนี้'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500 border-b border-gray-100 dark:border-slate-700">
                        <th className="px-2 py-2 font-semibold">ชื่อ</th>
                        <th className="px-2 py-2 font-semibold">อีเมล</th>
                        <th className="px-2 py-2 font-semibold">ที่มา</th>
                        <th className="px-2 py-2 font-semibold text-right">ยอดสะสม</th>
                        <th className="px-2 py-2 font-semibold">สถานะ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-slate-700/60">
                      {members.map((m) => (
                        <tr key={m.id} className="hover:bg-gray-50/60 dark:hover:bg-slate-700/40">
                          <td className="px-2 py-2 whitespace-nowrap">
                            <p className="font-semibold text-gray-800 dark:text-slate-100 truncate max-w-[200px]" title={m.name}>{m.name}</p>
                            {m.company_name && (
                              <p className="text-[11px] text-gray-400 dark:text-slate-500 truncate max-w-[200px]" title={m.company_name}>{m.company_name}</p>
                            )}
                          </td>
                          <td className="px-2 py-2 text-gray-600 dark:text-slate-300 whitespace-nowrap">{m.email}</td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <StatusBadge status={m.source} statusMap={MEMBER_SOURCE_MAP} />
                          </td>
                          <td className="px-2 py-2 text-right whitespace-nowrap">
                            <p className="text-gray-700 dark:text-slate-200">{fmtNum(m.tier_paid_orders)} order</p>
                            <p className="text-gray-500 dark:text-slate-400">{fmtBaht(m.tier_paid_amount)}</p>
                          </td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <StatusBadge status={m.is_active ? 'active' : 'inactive'} statusMap={MEMBER_STATUS_MAP} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {membersSupported && membersTotal > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100 dark:border-slate-700">
                  <p className="text-[12px] text-gray-500 dark:text-slate-400 whitespace-nowrap">
                    แสดง {fmtNum(pageStart)}–{fmtNum(pageEnd)} จาก {fmtNum(membersTotal)}
                  </p>
                  <div className="flex items-center gap-2">
                    <UiButton
                      variant="secondary"
                      icon={faChevronLeft}
                      disabled={!hasPrev || membersLoading}
                      onClick={() => setMembersOffset(Math.max(0, membersOffset - MEMBERS_PAGE_SIZE))}
                    >
                      ก่อนหน้า
                    </UiButton>
                    <UiButton
                      variant="secondary"
                      disabled={!hasNext || membersLoading}
                      onClick={() => setMembersOffset(membersOffset + MEMBERS_PAGE_SIZE)}
                    >
                      ถัดไป <FontAwesomeIcon icon={faChevronRight} className="ml-1" />
                    </UiButton>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </UiModal>
    </div>
  );
};

export default TiersPage;
